import {
  PageSection,
  PageWrapper,
  Price,
  PriceRow,
  StepPerNumberInput,
} from '@src/components';
import { useGetPackageQuery } from '@src/services/packages/packageApiSlice';
import {
  usePaymentMutation,
  useTopUpCheckoutMutation,
} from '@src/services/topUp/topUpApiSlice';
import { PackageCard } from '../../Package';
import { useEffect, useMemo, useState } from 'react';
import { useCheckResponse } from '@src/hooks';
import { ResponsiveModal } from '@src/components/ResponsiveModal';
import { Button } from 'antd';

type TopUpViewProps = {
  esimId: string;
  packageId: string;
  flag: string;
  countryName?: string;
  isOpen: boolean;
  onClose: () => void;
};
export function TopUpView({
  esimId,
  packageId,
  flag,
  countryName,
  isOpen,
  onClose,
}: Readonly<TopUpViewProps>) {
  const { checkResponse } = useCheckResponse();
  const [amountData, setAmountData] = useState(1);
  const [unitPrice, setUnitPrice] = useState<number>(0);
  const [currency, setCurrency] = useState<string>('USD');

  const { data: packageData, isLoading: getPackageLoading } =
    useGetPackageQuery({ id: packageId });

  const [
    checkout,
    {
      data: checkoutData,
      isLoading: isCheckoutLoading,
      error: checkoutError,
      isError: isCheckoutError,
    },
  ] = useTopUpCheckoutMutation();

  const [payment, { isLoading: isPaymentLoading }] = usePaymentMutation();

  useEffect(() => {
    checkout({ esimId: esimId, packageId: packageId, amount: amountData });
  }, [isOpen, esimId, packageId, checkout, amountData]);

  useEffect(() => {
    if (!isOpen) {
      setAmountData(1);
    }
  }, [isOpen]);

  useEffect(() => {
    if (checkoutData?.pricePerGB) {
      setUnitPrice(checkoutData.pricePerGB);
      setCurrency(checkoutData.currency ?? 'USD');
    }
  }, [checkoutData]);

  const totalPrice = useMemo(
    () => unitPrice * amountData,
    [unitPrice, amountData]
  );

  const errorMessage = useMemo(() => {
    if (!checkoutError) return null;
    return (
      (checkoutError as { data?: { message?: string } })?.data?.message ??
      'An error occurred during checkout.'
    );
  }, [checkoutError]);

  const isPayDisabled =
    !amountData || !checkoutData?.finalAmount || isCheckoutError;

  const handlePayment = async () => {
    try {
      const response = await payment({ esimId, packageId, amount: amountData });
      if ('data' in response && response.data?.paymentUrl) {
        window.open(response.data.paymentUrl, '_blank');
      } else {
        checkResponse(response.error);
      }
    } catch (error) {
      checkResponse({ error });
    }
  };

  return (
    <ResponsiveModal
      title='Top-Up'
      headerIcon={false}
      isOpen={isOpen}
      onClose={onClose}
      content={
        <PageWrapper
          loading={getPackageLoading}
          rootClassName='space-y-4 text-wl-on-background'
        >
          {packageData && (
            <PackageCard
              className='bg-wl-background'
              item={packageData}
              country={{ flag: flag, name: countryName ?? '' }}
              showPrice={false}
            />
          )}
          <p className='text-wl-on-background'>
            <b className='opacity-100'>Top-Up volume.</b>
            <span className='opacity-50'> Enter the volume you want</span>
          </p>
          <StepPerNumberInput
            step={1}
            max={1000}
            value={amountData}
            onChange={setAmountData}
          />

          {errorMessage && (
            <p className='text-red-500 text-sm'>{errorMessage}</p>
          )}
          <p>
            <b>Unit price</b>
          </p>
          <PageSection className='bg-wl-background text-wl-on-background p-4'>
            {unitPrice ? (
              <PriceRow
                label='1 GB'
                price={unitPrice}
                currency={currency}
                priceClassName='text-M-20'
              />
            ) : (
              <p className='text-wl-on-background'>
                Price per GB not available
              </p>
            )}
          </PageSection>
        </PageWrapper>
      }
      footer={
        <PageSection className='flex items-center w-full bg-wl-background p-4'>
          <span className='w-[30%] text-md truncate text-left'>
            <div className='text-wl-on-background text-M-20'>
              {<Price currency={currency} price={totalPrice} />}
            </div>
          </span>
          <Button
            loading={isCheckoutLoading || isPaymentLoading}
            className='w-[70%] btn-primary'
            onClick={handlePayment}
            disabled={isPayDisabled}
          >
            <p className='text-wl-on-primary'>Pay</p>
          </Button>
        </PageSection>
      }
    />
  );
}
